import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, FileText, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface NavDropdownProps {
  label: string;
  items: {
    name: string;
    link: string;
    icon?: React.ReactNode;
  }[];
  className?: string;
}

export function NavDropdown({ label, items, className }: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={cn("relative", className)}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className="relative px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-300 flex items-center gap-1.5 transition-colors"
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 mt-2 w-48 rounded-lg bg-white dark:bg-neutral-950 shadow-lg border border-gray-200 dark:border-neutral-800 overflow-hidden z-50"
          >
            <div className="py-1">
              {items.map((item, idx) => {
                const isInternalLink = item.link.startsWith("/");
                const content = (
                  <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 transition-colors hover:bg-gray-50 dark:hover:bg-neutral-900">
                    {item.icon && (
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {item.icon}
                      </span>
                    )}
                    <span>{item.name}</span>
                  </div>
                );

                if (isInternalLink) {
                  return (
                    <Link
                      key={idx}
                      to={item.link}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      {content}
                    </Link>
                  );
                }

                return (
                  <a
                    key={idx}
                    href={item.link}
                    onClick={() => setIsOpen(false)}
                    className="block"
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

